import { Component, ViewChild } from '@angular/core';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatButtonModule } from '@angular/material/button';
import { MatStepper, MatStepperModule } from '@angular/material/stepper';
import { CommonModule } from '@angular/common';
import { HttpStatusCode } from '@angular/common/http';
import { take } from 'rxjs';
import { NgOtpInputComponent, NgOtpInputModule } from 'ng-otp-input';
import { AuthService } from '../../../services/auth/auth.service';
import { AuthErrorCodes } from '../../../services/auth/auth-codes';
import { Router } from '@angular/router';
import { ButtonComponent } from '../../../shared/button/button.component';
import { InputComponent } from '../../../shared/input/input.component';
import * as loginConstants from '../../../shared/constants/loginconstants';
import { LocalStorageService } from '../../../services/local-storage/local-storage.service';
import { RouterLink } from '@angular/router';
import { ApiError } from '../../../services/api.service';

@Component({
  selector: 'app-login',
  standalone: true,
  imports: [
    MatFormFieldModule,
    MatCheckboxModule,
    MatIconModule,
    FormsModule,
    MatInputModule,
    MatButtonModule,
    MatStepperModule,
    ReactiveFormsModule,
    CommonModule,
    NgOtpInputModule,
    ButtonComponent,
    InputComponent,
    RouterLink,
  ],
  templateUrl: './login.component.html',
  styleUrl: './login.component.css',
})
export class LoginComponent {
  emailInputType = loginConstants.EMAIL_INPUT_TYPE;
  emailLabel = loginConstants.EMAIL_LABEL;
  buttonTwo = loginConstants.BUTTON_TWO;
  mailIcon = loginConstants.MAIL_ICON;
  textInputType = loginConstants.TEXT_INPUT_TYPE;
  passwordInputType = loginConstants.PASSWORD_INPUT_TYPE;
  passwordLabel = loginConstants.PASSWORD_LABEL;
  passwordVisibility = false;

  visibilityOffIcon = loginConstants.VISIBILITY_OFF_ICON;
  visibilityIcon = loginConstants.VISIBILITY_ICON;
  lockIcon = loginConstants.LOCK_ICON;
  pswPlaceholder = loginConstants.PSW_PLACEHOLDER;
  emailPlaceholder = loginConstants.EMAIL_PLACEHOLDER;
  btnMatRaised = loginConstants.BTN_MAT_RAISED;

  // 2FA / OTP step
  otpConfig = loginConstants.OTP_CONFIG;

  @ViewChild('stepper') stepper!: MatStepper;
  @ViewChild(NgOtpInputComponent) otpInput!: NgOtpInputComponent;
  isLinear = true;
  twoFactorRequired = false;
  verifiedEmail = '';
  otpError = false;
  private lastOtp = '';

  showErrorMsg: string = '';
  handlePswdVisibilityCallback(event: any) {
    this.passwordVisibility = !this.passwordVisibility;
  }

  emailMat = new FormControl('', [Validators.required, Validators.email]);
  password = new FormControl('', [Validators.required, Validators.maxLength(32)]);
  loginForm: FormGroup;
  hide = true;

  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private router: Router,
    private localStorageService: LocalStorageService
  ) {
    this.loginForm = this.fb.group({
      emailMat: this.emailMat,
      password: this.password,
      rememberMe: [false],
    });
  }

  ngOnInit(): void {
    const storedEmail = this.localStorageService.getItem('email');
    const storedPassword = this.localStorageService.getItem('password');
    const rememberMe = this.localStorageService.getItem('rememberMe');

    if (storedEmail && storedPassword && rememberMe === 'true') {
      this.emailMat.setValue(storedEmail);
      this.password.setValue(storedPassword);
      this.loginForm.get('rememberMe')?.setValue(true);
    }
  }

  onSubmit(): void {
    if (this.loginForm.valid) {
      const { emailMat, password, rememberMe } = this.loginForm.value;
      this.authService
        .login({ email: emailMat, password, rememberMe })
        .pipe(take(1))
        .subscribe({
          next: (result) => {
            this.navigateAfterAuth({ eulaAccepted: Boolean(result.eulaAccepted) });
          },
          error: (err) => {
            const error = err as ApiError;
            if (error.status === HttpStatusCode.Forbidden && error.errorCode === AuthErrorCodes.TwoFactorRequired) {
              // Backend emailed an OTP; advance to the verification step.
              this.twoFactorRequired = true;
              this.verifiedEmail = emailMat;
              this.showErrorMsg = '';
              this.stepper.next();
              return;
            }
            this.showErrorMsg =
              error.status === HttpStatusCode.Unauthorized
                ? $localize`Invalid Credentials!`
                : $localize`Error while logging in.`;
          },
        });
    }
  }

  onOtpChange(event: string): void {
    if (event.length !== this.otpConfig.length) {
      // Partial code: clear the guard so re-entering the same digits validates again.
      this.lastOtp = '';
      this.otpError = false;
      return;
    }
    // Guard against re-validating a code we have already sent. ng-otp-input re-emits the full
    // value on any keyup over a filled box (Enter, Tab, the key release after a paste), so
    // without this a single entry burns several attempts against the backend's 2FA counter.
    if (this.lastOtp === event) {
      return;
    }
    this.lastOtp = event;
    this.authService
      .verifyTwoFactor(event)
      .pipe(take(1))
      .subscribe({
        next: (result) => {
          this.otpError = false;
          this.navigateAfterAuth({ eulaAccepted: Boolean(result.eulaAccepted) });
        },
        error: () => {
          this.otpError = true;
        },
      });
  }

  resendOtp(): void {
    this.otpInput?.setValue('');
    this.otpError = false;
    this.lastOtp = '';
    this.authService
      .resendTwoFactorCode()
      .pipe(take(1))
      .subscribe({
        error: () => {
          this.showErrorMsg = $localize`Error while resending the code.`;
        },
      });
  }

  private navigateAfterAuth({ eulaAccepted }: { eulaAccepted: boolean }): void {
    if (!eulaAccepted) {
      this.router.navigate(['/license-agreement']);
    } else if (this.authService.snapshot?.userRole === 'SuperAdmin') {
      this.router.navigate(['/org']);
    } else {
      this.router.navigate(['/home']);
    }
  }
}
